import React, { useContext, useEffect, useMemo } from 'react'
import FButton from 'components/BaseComponents/FButton'
import FNeedHelpCTA from 'components/BaseComponents/FNeedHelpCTA'
import { useSTheme } from '../../../../../styles/theme'
import { useTranslation } from '../../../../../i18n'
import * as Text from 'components/BaseComponents/Text'
import { FormProvider, useForm } from 'react-hook-form'
import { Box } from 'rebass'
import { StepWizardChildProps } from 'react-step-wizard'
import { input } from '../../../../../components/BaseComponents/constants'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { ga4_useTitle } from 'utils/ga4'
import FInputRadioGroup from 'components/BaseComponents/FInputRadioGroup'
import { LBTextHeader, LBTextSubHeader } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useAddApplicationAnswersMutation } from 'types/graphql'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import SInputError from 'components/opinionless/SInputErrorV2'

interface Props {
    onPrevious: () => void
    onNextStep: () => void
    personId: string
}

const SSIBenefitsIntroduction: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const form = useForm({ shouldFocusError: false })
    const { handleSubmit } = form
    const [addApplicationAnswers] = useAddApplicationAnswersMutation()
    const { refetch: refetchApplicationAnswers } = useApplicationAnswers()
    const applicationContext = useContext(ApplicationContext)

    ga4_useTitle('SSI introduction')

    const paragraphArray = [
        t('ssi_benefits_wizard_introduction_paragraph_one'),
        t('ssi_benefits_wizard_introduction_paragraph_two'),
    ]

    const radioDataArray = useMemo(() => {
        return [
            {
                text: t('ssi_options_me_only'),
                id: 'ssi_options_me_only',
            },
            {
                text: t('ssi_options_other_members'),
                id: 'ssi_options_other_members',
            },
        ]
    }, [t])

    const onSubmit = (values) => {
        addApplicationAnswers({
            variables: {
                answers: [
                    {
                        personApplicationId: applicationContext.application.id,
                        category: 'SSI_BENEFITS',
                        response: values.headStartQuestions,
                    },
                ],
            },
        }).then((response) => {
            if (response.data.addApplicationAnswers.success === true) {
                void refetchApplicationAnswers()
                props.onNextStep()
            } else {
                form.setError('ssiIntroduction', { type: 'oops', message: t('error') })
            }
        })
    }
    useEffect(() => {
        if (
            applicationContext?.answers?.length &&
            applicationContext?.answers.find((itm) => itm.category === 'SSI_BENEFITS')
        ) {
            form.setValue(
                'headStartQuestions',
                applicationContext?.answers.find((itm) => itm.category === 'SSI_BENEFITS').response
            )
        }
    }, [applicationContext])

    return (
        <FormProvider {...form}>
            <Box as="form" onSubmit={handleSubmit(onSubmit)}>
                <FButtonPrevious onClick={props.onPrevious} />
                <LBTextHeader
                    sx={{
                        textAlign: 'left',
                        fontSize: '32px',
                        color: theme.colors.secondaryColor,
                        lineHeight: '120%',
                        fontFamily: 'Raleway',
                        fontWeight: '900',
                    }}
                >
                    {t('ssi_benefits_wizard_introduction_header')}
                </LBTextHeader>

                <LBTextSubHeader
                    dangerouslySetInnerHTML={{
                        __html: t('ssi_benefits_wizard_introduction_sub_header'),
                    }}
                />

                <Text.FParagraphGroup paragraphs={paragraphArray} />

                <LBTextSubHeader
                    dangerouslySetInnerHTML={{
                        __html: t('ssi_benefits_wizard_introduction_paragraph_three'),
                    }}
                />

                <FInputRadioGroup
                    radioDataArray={radioDataArray}
                    name={'headStartQuestions'}
                    id={'headStartQuestions'}
                    minimumSelection={1}
                />

                <FButton
                    tabIndex={0}
                    type={'submit'}
                    title={t('continue')}
                    id={'continueButton'}
                    width={'100%'}
                    style={{ marginTop: input.desktop.marginTop }}
                    ga4_key={t('continue', { lang: 'en' })}
                >
                    {t('save_and_continue')}
                </FButton>
                {form.errors.ssiIntroduction && (
                    <SInputError
                        sx={{
                            color: theme.colors.redError,
                        }}
                        forElementId={'continueButton'}
                    >
                        {t(form.errors.ssiIntroduction?.message)}
                    </SInputError>
                )}
            </Box>
        </FormProvider>
    )
}

export default SSIBenefitsIntroduction
